import { useEffect, useState } from "react";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { MeshGroundFrame, MeshWorldCenter } from "@/utils/satelliteGroundPlane";
import {
  layoutsForMesh,
  prepareRoofMesh,
  type RoofBoundary,
} from "@/utils/roofMeshScene";
import type { PanelMount, RoofMeshLayout } from "@/utils/roofMeshPanelPlacement";

export type RoofMeshPlacement = {
  align: MeshWorldCenter;
  layouts: Record<PanelMount, RoofMeshLayout>;
};

/**
 * Load the roof mesh and place panels while the satellite tab is showing.
 * The 3D viewer uses the same prepare step when it opens.
 */
export function useRoofMeshPlacement(input: {
  meshUrl?: string | null;
  groundFrame?: MeshGroundFrame | null;
  propertyBoundary?: RoofBoundary;
  satelliteImageUrl?: string | null;
}): RoofMeshPlacement | null {
  const { meshUrl, groundFrame, propertyBoundary, satelliteImageUrl } = input;
  const [placement, setPlacement] = useState<RoofMeshPlacement | null>(null);
  const frameKey = groundFrame
    ? [
      groundFrame.originLongitude,
      groundFrame.originLatitude,
      groundFrame.eastLongitudePerM,
      groundFrame.eastLatitudePerM,
      groundFrame.southLongitudePerM,
      groundFrame.southLatitudePerM,
    ].join(",")
    : "";
  const boundaryKey = propertyBoundary ? JSON.stringify(propertyBoundary) : "";

  useEffect(() => {
    setPlacement(null);
    if (!meshUrl || !groundFrame) return;
    let cancelled = false;
    const loader = new GLTFLoader();
    loader.load(
      meshUrl,
      (gltf) => {
        if (cancelled) return;
        const prepared = prepareRoofMesh(gltf.scene, {
          groundFrame,
          propertyBoundary: propertyBoundary ?? null,
          satelliteImageUrl: satelliteImageUrl ?? null,
          cacheKey: meshUrl,
        });
        setPlacement({
          align: { x: prepared.align.x, z: prepared.align.z },
          layouts: layoutsForMesh(prepared.cacheKey, gltf.scene),
        });
      },
      undefined,
      () => {
        if (!cancelled) setPlacement(null);
      },
    );
    return () => {
      cancelled = true;
    };
  // frameKey and boundaryKey stand in for the object identities.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meshUrl, frameKey, boundaryKey, satelliteImageUrl]);

  return placement;
}
